import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export default function Products({ projects = [] }) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const disableMotion = prefersReducedMotion || isMobile;

  const handleProjectClick = (id) => {
    navigate("/products-view", { state: { id, projects } });
  };

  const handleViewProjectClick = (value) => {
    navigate("/products", { state: { filter: value } });
  };


  return (
    <section id="projects" className="relative min-h-screen py-20">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <motion.h2
          className="text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: disableMotion ? 0 : 0.4 }}
          viewport={{ once: true }}
        >
          <span className="text-secondary">OnGoing</span> Projects
        </motion.h2>

        <motion.div
          className="max-w-3xl mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: disableMotion ? 0 : 0.4, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <p className="text-lg text-slate-200 leading-relaxed">
            Explore our portfolio of successfully completed construction projects that showcase our commitment to quality,
            innovation, and excellence. Each project represents our dedication to turning dreams into reality.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {projects.map((doc, i) => (doc.projectStatus === 'In Progress' && (
            <motion.article
              key={doc.id || i}
              className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: disableMotion ? 0 : 0.4,
                delay: disableMotion ? 0 : Math.min(i * 0.05, 0.5)
              }}
              viewport={{ once: true, margin: '0px 0px -50px 0px' }}
              whileHover={disableMotion ? {} : { y: -4 }}
              whileTap={disableMotion ? {} : { scale: 0.98 }}
            >
              <div
                className="h-48 bg-cover bg-center transition-transform duration-500 group-hover:scale-110 overflow-hidden"
                style={{
                  backgroundImage: `url(${doc.coverImage ? doc.coverImage : 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400'})`
                }}
              />
              <div className="p-4">
                <h3 className="text-lg font-heading font-semibold text-white mb-2 line-clamp-1 group-hover:text-secondary transition-colors duration-300">
                  {doc.title}
                </h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {doc.location && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white text-black">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {doc.location}
                    </span>
                  )}
                  {doc.completedYear && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-black">
                      {doc.completedYear}
                    </span>
                  )}
                </div>
                <p className="text-sm text-white line-clamp-2 mb-4">
                  {doc.description}
                </p>
                <div className="flex justify-end">
                  <motion.button
                    onClick={() => handleProjectClick(doc.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-secondary text-primary-dark rounded-lg hover:bg-secondary-dark transition-all duration-300 font-semibold w-full justify-center"
                    whileHover={disableMotion ? {} : {
                      y: -2,
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                    }}
                    whileTap={disableMotion ? {} : { scale: 0.98 }}
                  >
                    <span>View Details</span>
                    <svg
                      className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </motion.button>
                </div>
              </div>
            </motion.article>
          )))}

          {/* View All Card */}
          <motion.div
            className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 shadow-lg flex flex-col min-h-[300px]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: disableMotion ? 0 : 0.4,
              delay: disableMotion ? 0 : Math.min(projects.length * 0.05, 0.5)
            }}
            viewport={{ once: true, margin: '0px 0px -50px 0px' }}
            whileHover={disableMotion ? {} : { y: -4 }}
            whileTap={disableMotion ? {} : { scale: 0.98 }}
            onClick={() => handleViewProjectClick("In Progress")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleViewProjectClick("In Progress")}
          >
            <div className="text-center p-8 flex flex-col flex-grow w-full items-center">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-2xl text-white font-heading font-bold text-gray-900 mb-2 group-hover:text-secondary transition-colors">
                View All Projects
              </h3>
              <p className="text-white mb-4">
                Explore our complete portfolio
              </p>
              <motion.div
                className="flex items-center justify-center mb-0 space-x-2 px-4 py-2 bg-secondary text-primary-dark rounded-lg hover:bg-secondary-dark transition-all duration-300 font-semibold mt-auto"
                whileHover={disableMotion ? {} : {
                  y: -2,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                }}
                whileTap={disableMotion ? {} : { scale: 0.98 }}
              >
                <span>See More</span>
                <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <motion.h2
          className="text-4xl lg:text-5xl font-heading font-bold text-white mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: disableMotion ? 0 : 0.4 }}
          viewport={{ once: true }}
        >
          <span className="text-secondary">Completed</span> Projects
        </motion.h2>

        {/* <motion.div
          className="max-w-3xl mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: disableMotion ? 0 : 0.4, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <p className="text-lg text-slate-200 leading-relaxed">
            Explore our portfolio of successfully completed construction projects that showcase our commitment to quality,
            innovation, and excellence. Each project represents our dedication to turning dreams into reality.
          </p>
        </motion.div> */}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {projects.map((doc, i) => (doc.projectStatus === 'Completed' && (
            <motion.article
              key={doc.id || i}
              className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: disableMotion ? 0 : 0.4,
                delay: disableMotion ? 0 : Math.min(i * 0.05, 0.5)
              }}
              viewport={{ once: true, margin: '0px 0px -50px 0px' }}
              whileHover={disableMotion ? {} : { y: -4 }}
              whileTap={disableMotion ? {} : { scale: 0.98 }}
            >
              <div
                className="h-48 bg-cover bg-center transition-transform duration-500 group-hover:scale-110 overflow-hidden"
                style={{
                  backgroundImage: `url(${doc.coverImage ? doc.coverImage : 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400'})`
                }}
              />
              <div className="p-4">
                <h3 className="text-lg font-heading font-semibold text-white mb-2 line-clamp-1 group-hover:text-secondary transition-colors duration-300">
                  {doc.title}
                </h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {doc.location && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white text-black">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {doc.location}
                    </span>
                  )}
                  {doc.completedYear && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-black">
                      {doc.completedYear}
                    </span>
                  )}
                </div>
                <p className="text-sm text-white line-clamp-2 mb-4">
                  {doc.description}
                </p>
                <div className="flex justify-end">
                  <motion.button
                    onClick={() => handleProjectClick(doc.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-secondary text-primary-dark rounded-lg hover:bg-secondary-dark transition-all duration-300 font-semibold w-full justify-center"
                    whileHover={disableMotion ? {} : {
                      y: -2,
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                    }}
                    whileTap={disableMotion ? {} : { scale: 0.98 }}
                  >
                    <span>View Details</span>
                    <svg
                      className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </motion.button>
                </div>
              </div>
            </motion.article>
          )))}

          {/* View All Card */}
          <motion.div
            className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 shadow-lg flex flex-col min-h-[300px]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: disableMotion ? 0 : 0.4,
              delay: disableMotion ? 0 : Math.min(projects.length * 0.05, 0.5)
            }}
            viewport={{ once: true, margin: '0px 0px -50px 0px' }}
            whileHover={disableMotion ? {} : { y: -4 }}
            whileTap={disableMotion ? {} : { scale: 0.98 }}
            onClick={() => handleViewProjectClick("Completed")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleViewProjectClick("Completed")}
          >
            <div className="text-center p-8 flex flex-col flex-grow w-full items-center">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-2xl text-white font-heading font-bold text-gray-900 mb-2 group-hover:text-secondary transition-colors">
                View All Projects
              </h3>
              <p className="text-white mb-4">
                Explore our complete portfolio
              </p>
              <motion.div
                className="flex items-center justify-center mb-0 space-x-2 px-4 py-2 bg-secondary text-primary-dark rounded-lg hover:bg-secondary-dark transition-all duration-300 font-semibold mt-auto"
                whileHover={disableMotion ? {} : {
                  y: -2,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                }}
                whileTap={disableMotion ? {} : { scale: 0.98 }}
              >
                <span>See More</span>
                <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
